import React from 'react';
import { ConnectorRegistryEntry, ConnectorConnection } from '../../services/api';
import { messageForStatus, NEEDS_RECONNECT_PILL, USE_FOR_LABELS } from './copy';

/**
 * ConnectorCard — single tile in the Connectors modal grid.
 *
 * Layout (compact since the modal holds 15 connectors in labelled groups):
 *   ┌─────────────────────────────────────────┐
 *   │  [icon 40]  Display name                │
 *   │             Tagline (2 lines, w/60)     │
 *   │  [status pill]              [Manage →]  │
 *   └─────────────────────────────────────────┘
 *
 * Banned-words rule (inherited from SecretChip.tsx design spec):
 *   - Surface "App connection" / "Connect" / "Disconnect" / "Reconnect"
 *   - NEVER "OAuth", "API key", "token", "MCP server", "endpoint",
 *     "client_id" in any visible string.
 */
export interface ConnectorCardProps {
  entry: ConnectorRegistryEntry;
  connection: ConnectorConnection | null;
  onConnect: (id: string) => void;
  onManage: (id: string) => void;
  isBusy?: boolean;
  // Bring-your-own only: a one-line outcome under the buttons (a failed
  // connect, "Try again in N seconds").
  message?: string | null;
}

// Bring-your-own (connectMode 'connect') states as the card shows them.
// Anything else the server sends falls to 'unknown': generic copy (DX-17).
type ByoCardState = 'not_connected' | 'connected' | 'needs_reauth' | 'needs_setup' | 'unknown';
function byoCardState(connection: ConnectorConnection | null): ByoCardState {
  if (!connection || connection.status === 'disconnected') return 'not_connected';
  if (connection.status === 'connected' || connection.status === 'error') return 'connected';
  if (connection.status === 'needs_reauth') return 'needs_reauth';
  if (connection.status === 'needs_setup') return 'needs_setup';
  return 'unknown';
}

const ConnectorCard: React.FC<ConnectorCardProps> = ({
  entry,
  connection,
  onConnect,
  onManage,
  isBusy,
  message,
}) => {
  const isByo = entry.connectMode === 'connect';
  const byoState = isByo ? byoCardState(connection) : null;
  // Treat both 'connected' (legacy OAuth) and 'enabled' (universal-key)
  // as the live/in-use state so the green Connected pill + Manage button
  // surface for both shapes.
  const isConnected = isByo
    ? byoState === 'connected'
    : !!connection &&
      (connection.status === 'connected' || connection.status === 'enabled');
  const isError = !isByo && connection && connection.status === 'error';
  const needsReconnect = byoState === 'needs_reauth' || byoState === 'needs_setup';
  const statusLine =
    byoState === 'unknown' || needsReconnect ? messageForStatus(connection && connection.status, entry.displayName) : null;
  const useFor = isByo ? USE_FOR_LABELS[entry.id] : undefined;
  // D-16: the note sits right above the button that leads to the consent page.
  const connectNote = isByo && !isConnected ? entry.connectNote : undefined;

  const primaryBtn =
    'inline-flex items-center justify-center h-8 max-md:h-auto max-md:min-h-[48px] px-3 max-md:px-4 rounded-lg bg-primary-500 hover:bg-brand-blue-hover text-white text-[13px] font-semibold transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 focus-visible:ring-offset-[#141416] disabled:opacity-60 disabled:cursor-not-allowed';
  const secondaryBtn =
    'inline-flex items-center justify-center h-8 max-md:h-auto max-md:min-h-[48px] px-3 max-md:px-4 rounded-lg bg-white/[0.06] hover:bg-white/[0.1] border border-white/[0.08] hover:border-white/[0.14] text-white text-[13px] font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 focus-visible:ring-offset-[#141416] disabled:opacity-60 disabled:cursor-not-allowed';
  const pill = 'inline-flex shrink-0 whitespace-nowrap items-center gap-1.5 text-xs font-medium';

  return (
    <article
      aria-labelledby={`connector-${entry.id}-name`}
      className={`group relative rounded-xl p-4 flex flex-col border transition-colors duration-150 ${
        isConnected
          ? 'bg-emerald-500/[0.03] border-emerald-400/[0.14] hover:border-emerald-400/25'
          : 'bg-white/[0.025] border-white/[0.06] hover:bg-white/[0.04] hover:border-white/[0.12]'
      }`}
    >
      <div className="flex items-start gap-3">
        {/* Brand icon — 40px white tile so every logo reads on the dark card */}
        <div className="w-10 h-10 rounded-[10px] bg-white p-[7px] flex items-center justify-center shrink-0 ring-1 ring-black/5 shadow-sm">
          <img
            src={entry.brandIcon}
            alt=""
            aria-hidden="true"
            className="w-full h-full object-contain"
            onError={(e) => {
              (e.currentTarget as HTMLImageElement).style.visibility = 'hidden';
            }}
          />
        </div>
        <div className="min-w-0 pt-px">
          <h4
            id={`connector-${entry.id}-name`}
            className="text-[15px] leading-5 font-semibold text-white truncate"
            style={{ fontFamily: 'DM Sans, sans-serif' }}
          >
            {entry.displayName}
          </h4>
          {useFor && (
            <p className="mt-0.5 text-xs font-medium text-white/60" data-testid={`connector-${entry.id}-usefor`}>
              {useFor}
            </p>
          )}
          <p
            className="mt-1 text-[13px] text-white/50 leading-[1.45]"
            style={{
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }}
          >
            {entry.tagline}
          </p>
        </div>
      </div>

      {connectNote && (
        <p
          className="mt-3 px-3 py-2 rounded-lg text-xs leading-snug bg-white/[0.03] text-white/65 border border-white/[0.06]"
          data-testid={`connector-${entry.id}-note`}
        >
          {connectNote}
        </p>
      )}

      <div className="mt-auto pt-4 flex flex-wrap items-center justify-between gap-x-3 gap-y-2">
        {isConnected ? (
          <span
            aria-label={`${entry.displayName} is connected`}
            className={`${pill} text-emerald-300`}
          >
            <span aria-hidden="true" className="w-1.5 h-1.5 rounded-full bg-emerald-400 shadow-[0_0_0_3px_rgba(52,211,153,0.15)]" />
            <span>Connected</span>
          </span>
        ) : isError ? (
          <span
            aria-label={`${entry.displayName} needs reconnecting`}
            className={`${pill} text-amber-300`}
          >
            <span aria-hidden="true" className="w-1.5 h-1.5 rounded-full bg-amber-400" />
            <span>Reconnect</span>
          </span>
        ) : isBusy ? (
          // Connecting: the status is changing, and the pill plus "Connecting…"
          // is wider than a card in the three-across grid.
          <span aria-hidden="true" />
        ) : needsReconnect ? (
          <span
            aria-label={`${entry.displayName} needs reconnecting`}
            className={`${pill} text-amber-300`}
          >
            <span aria-hidden="true" className="w-1.5 h-1.5 rounded-full bg-amber-400" />
            <span>{NEEDS_RECONNECT_PILL}</span>
          </span>
        ) : (
          <span
            aria-label={`${entry.displayName} is not connected`}
            className={`${pill} text-white/40`}
          >
            <span aria-hidden="true" className="w-1.5 h-1.5 rounded-full bg-white/25" />
            <span>Not connected</span>
          </span>
        )}

        {isConnected ? (
          <button
            type="button"
            onClick={() => onManage(entry.id)}
            className="inline-flex items-center gap-1 h-8 max-md:h-auto max-md:min-h-[44px] -mr-2 px-2 rounded-lg text-[13px] font-medium text-white/70 hover:text-white hover:bg-white/[0.06] transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
            data-testid={`manage-${entry.id}`}
          >
            Manage
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className="transition-transform group-hover:translate-x-0.5">
              <path d="m9 18 6-6-6-6" />
            </svg>
          </button>
        ) : needsReconnect ? (
          <button
            type="button"
            disabled={isBusy}
            onClick={() => onConnect(entry.id)}
            aria-label={`Reconnect ${entry.displayName}`}
            // ml-auto: if the pill and button don't fit on one line, the
            // button wraps and stays right-aligned.
            className={`${primaryBtn} ml-auto`}
            data-testid={`reconnect-${entry.id}`}
          >
            {isBusy ? 'Connecting…' : 'Reconnect'}
          </button>
        ) : (
          <button
            type="button"
            disabled={isBusy}
            onClick={() => onConnect(entry.id)}
            aria-label={`Connect ${entry.displayName}`}
            className={isError ? primaryBtn : secondaryBtn}
            data-testid={`connect-${entry.id}`}
          >
            {isBusy ? 'Connecting…' : 'Connect'}
          </button>
        )}
      </div>

      {(statusLine || message) && (
        <p
          role="status"
          className="mt-3 text-xs text-white/55 leading-snug"
          data-testid={`connector-${entry.id}-message`}
        >
          {message || statusLine}
        </p>
      )}
    </article>
  );
};

export default ConnectorCard;
